import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { showErrorToast } from '@/utils/toast';

/** Quiet period after which the refresh burst-counter resets. */
const REFRESH_ATTEMPT_WINDOW_MS = 60_000;

interface SessionState {
  isValid: boolean;
  expiresAt: number | null;
  isRefreshing: boolean;
  lastActivity: number;
  minutesUntilExpiry: number;
}

/**
 * Smart session monitoring hook
 * Silently refreshes sessions for active users
 * Only shows warnings for truly inactive sessions
 */
export function useSessionMonitor() {
  const { user, session } = useAuth();
  const [sessionState, setSessionState] = useState<SessionState>({
    isValid: !!session,
    expiresAt: session?.expires_at ? parseInt(session.expires_at) * 1000 : null,
    isRefreshing: false,
    lastActivity: Date.now(),
    minutesUntilExpiry: 0,
  });

  const refreshAttemptRef = useRef(0);
  /** When a refresh was last ATTEMPTED — lets the burst counter above decay. */
  const lastRefreshAttemptAtRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout>();
  const activityListenerRef = useRef<boolean>(false);

  // Calculate minutes until expiry
  const getMinutesUntilExpiry = (expiresAt: number | null): number => {
    if (!expiresAt) return 0;
    return Math.max(0, Math.floor((expiresAt - Date.now()) / (1000 * 60)));
  };

  // Track user activity
  const updateLastActivity = () => {
    setSessionState((prev) => ({ ...prev, lastActivity: Date.now() }));
  };

  // Check if user is active (any activity in last 24 hours)
  const isUserActive = (): boolean => {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return sessionState.lastActivity > twentyFourHoursAgo;
  };

  // Silently refresh session for active users.
  //
  // The attempt counter is a BURST guard, not a lifetime budget. It used to
  // reset ONLY after a successful refresh, so three transient failures
  // (offline, flaky wifi, a tunnel) permanently disarmed refreshing for the
  // lifetime of the page — and this hook mounts on every authenticated route.
  // The user was then force-signed-out when the token expired an hour later
  // WITH A STILL-VALID REFRESH TOKEN, losing unsaved composer input. The
  // visibility/focus recovery path was disarmed by the same counter, so coming
  // back online could not rescue it either.
  //
  // Decay it instead: consecutive rapid failures still back off, but a quiet
  // minute restores the budget.
  const attemptSessionRefresh = async (silent: boolean = false): Promise<boolean> => {
    if (
      lastRefreshAttemptAtRef.current > 0 &&
      Date.now() - lastRefreshAttemptAtRef.current > REFRESH_ATTEMPT_WINDOW_MS
    ) {
      refreshAttemptRef.current = 0;
    }
    if (sessionState.isRefreshing || refreshAttemptRef.current >= 3) {
      return false;
    }
    lastRefreshAttemptAtRef.current = Date.now();

    try {
      setSessionState((prev) => ({ ...prev, isRefreshing: true }));
      refreshAttemptRef.current += 1;

      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        if (!silent && import.meta.env.DEV) {
          logger.warn('Session refresh failed:', error?.message);
        }
        return false;
      }

      // Reset state on successful refresh
      refreshAttemptRef.current = 0;

      setSessionState({
        isValid: true,
        expiresAt: parseInt(data.session.expires_at) * 1000,
        isRefreshing: false,
        lastActivity: Date.now(),
        minutesUntilExpiry: getMinutesUntilExpiry(parseInt(data.session.expires_at) * 1000),
      });

      // Only log in development for debugging
      if (import.meta.env.DEV && !silent) {
        logger.debug('Session refreshed successfully');
      }

      return true;
    } catch (error) {
      if (!silent && import.meta.env.DEV) {
        logger.error('Session refresh error:', error);
      }
      return false;
    } finally {
      setSessionState((prev) => ({ ...prev, isRefreshing: false }));
    }
  };

  // Handle true session expiration (only for inactive users)
  const handleSessionExpired = async () => {
    // Clear any stale session data
    await supabase.auth.signOut();

    showErrorToast(
      'Session Expired',
      'Your session has expired due to inactivity. Please sign in again.',
      {
        duration: 0, // Persistent until dismissed
        action: {
          label: 'Sign In',
          onClick: () => {
            window.location.href = '/auth';
          },
        },
      },
    );

    // Redirect after a brief delay
    setTimeout(() => {
      window.location.href = '/auth';
    }, 2000);
  };

  // Set up activity listeners
  useEffect(() => {
    if (!user || !session || activityListenerRef.current) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      updateLastActivity();
    };

    // Add activity listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    activityListenerRef.current = true;

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      activityListenerRef.current = false;
    };
  }, [user, session]);

  // F-107 (UAT 2026-06-13): refresh proactively when a tab regains focus.
  //
  // The 30s setInterval below is the ONLY thing that fires the near-expiry
  // refresh — and browsers FREEZE timers in backgrounded/inactive tabs (Chrome
  // throttles hidden-tab timers to ~1/min and can suspend them entirely after a
  // few minutes). So a tab left open or backgrounded for an hour never reaches
  // the refresh branch, the access token quietly expires, and the user's next
  // action (a chat send, any REST query) 401s with no warning. That's the
  // long-open-tab failure Clayton hit (Sentry: PGRST303 JWT-expired storm →
  // chat `{"error":"Unauthorized"}`). Refreshing the instant the tab becomes
  // visible / the window refocuses / the network returns closes the hole BEFORE
  // the next request leaves — no page reload, no lost input.
  useEffect(() => {
    if (!user || !session) return;

    let inFlight = false;
    const refreshIfStale = async () => {
      if (inFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session?.expires_at) return; // mock auth / no expiry — nothing to do
        // `Session.expires_at` is unix SECONDS (a number) — use it directly.
        const msLeft = data.session.expires_at * 1000 - Date.now();
        // Refresh if already expired or within the same 10-min window the
        // interval uses, so a freshly-focused tab never fires a request on a
        // dead (or about-to-die) token.
        if (msLeft <= 10 * 60 * 1000) {
          await attemptSessionRefresh(true);
        }
      } catch {
        /* best-effort; the interval + reactive guards remain as backstops */
      } finally {
        inFlight = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshIfStale();
    };
    const onFocusOrOnline = () => void refreshIfStale();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocusOrOnline);
    window.addEventListener('online', onFocusOrOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocusOrOnline);
      window.removeEventListener('online', onFocusOrOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session]);

  // Session monitoring loop
  useEffect(() => {
    if (!user || !session?.expires_at) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    const expiresAt = parseInt(session.expires_at) * 1000;

    // Update initial state
    setSessionState({
      isValid: true,
      expiresAt,
      isRefreshing: false,
      lastActivity: Date.now(),
      minutesUntilExpiry: getMinutesUntilExpiry(expiresAt),
    });

    // Monitor session every 30 seconds
    intervalRef.current = setInterval(() => {
      if (expiresAt === null) return; // Skip if no expiry is set (e.g., mock auth)

      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60));

      setSessionState((prev) => ({
        ...prev,
        minutesUntilExpiry,
        isValid: timeUntilExpiry > 0,
      }));

      // Session already expired
      if (timeUntilExpiry <= 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        // F-107 (UAT 2026-06-13): an expired ACCESS token is normal and
        // recoverable — the refresh token long outlives it (~weeks vs ~1h). The
        // old code went straight to signOut + /auth redirect here, evicting
        // users (and their unsaved chat input) on a routine expiry. Try a silent
        // refresh first; only force re-auth if the refresh token is itself dead.
        void attemptSessionRefresh(true).then((ok) => {
          if (!ok) handleSessionExpired();
        });
        return;
      }

      // Smart refresh logic
      // Always try to refresh the session silently when it's near expiry (<= 10 mins).
      // This prevents force-logging out users who simply stepped away from their computer.
      if (minutesUntilExpiry <= 10) {
        attemptSessionRefresh(true);
      }
    }, 30000); // Check every 30 seconds

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user, session]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    sessionState,
    refreshSession: () => attemptSessionRefresh(false),
    isSessionValid: sessionState.isValid,
    minutesUntilExpiry: sessionState.minutesUntilExpiry,
    isRefreshing: sessionState.isRefreshing,
    isUserActive: isUserActive(),
  };
}
